import { stripIndent } from 'common-tags';
import { OrderPending } from './components/OrderPending';

export const orderPendingEmailTemplate = {
  label: 'Awaiting Payment Instructions',
  description: 'Sent when an order is placed with a pay-by-bank method (Zelle) and awaits payment',
  icon: 'TimeLine',
  component: OrderPending,
  defaultTemplate: {
    name: 'Awaiting Payment Instructions',
    subject: {
      en: 'Complete your payment for order #{{ orderNumber }} — {{ storeName }}',
      fr: 'Finalisez le paiement de votre commande #{{ orderNumber }} — {{ storeName }}',
      it: 'Completa il pagamento del tuo ordine #{{ orderNumber }} — {{ storeName }}',
      es: 'Completa el pago de tu pedido #{{ orderNumber }} — {{ storeName }}',
      de: 'Schließe die Zahlung deiner Bestellung #{{ orderNumber }} ab — {{ storeName }}',
    },
    templateVariables: {
      order_pending: {
        body: {
          en: stripIndent`
            <p>Hi {{ customerFirstName }},</p>
            <p>We received your order and are holding it for you. It will be processed as soon as your payment arrives.</p>
            <p><strong>Order:</strong> #{{ orderNumber }}<br/>
            <strong>Amount to send:</strong> {{ orderTotal }}<br/>
            <strong>Send to:</strong> {{ zelleMerchantEmail }}<br/>
            <strong>Memo / reference:</strong> #{{ orderNumber }}</p>
          `,
          fr: stripIndent`
            <p>Bonjour {{ customerFirstName }},</p>
            <p>Nous avons bien reçu votre commande et nous la gardons pour vous. Elle sera traitée dès réception de votre paiement.</p>
            <p><strong>Commande :</strong> #{{ orderNumber }}<br/>
            <strong>Montant à envoyer :</strong> {{ orderTotal }}<br/>
            <strong>Destinataire :</strong> {{ zelleMerchantEmail }}<br/>
            <strong>Mémo / référence :</strong> #{{ orderNumber }}</p>
          `,
          it: stripIndent`
            <p>Ciao {{ customerFirstName }},</p>
            <p>Abbiamo ricevuto il tuo ordine e lo teniamo da parte per te. Sarà elaborato non appena riceveremo il pagamento.</p>
            <p><strong>Ordine:</strong> #{{ orderNumber }}<br/>
            <strong>Importo da inviare:</strong> {{ orderTotal }}<br/>
            <strong>Destinatario:</strong> {{ zelleMerchantEmail }}<br/>
            <strong>Causale / riferimento:</strong> #{{ orderNumber }}</p>
          `,
          es: stripIndent`
            <p>Hola {{ customerFirstName }},</p>
            <p>Hemos recibido tu pedido y lo estamos reservando. Se procesará en cuanto recibamos tu pago.</p>
            <p><strong>Pedido:</strong> #{{ orderNumber }}<br/>
            <strong>Importe a enviar:</strong> {{ orderTotal }}<br/>
            <strong>Enviar a:</strong> {{ zelleMerchantEmail }}<br/>
            <strong>Concepto / referencia:</strong> #{{ orderNumber }}</p>
          `,
          de: stripIndent`
            <p>Hallo {{ customerFirstName }},</p>
            <p>Wir haben deine Bestellung erhalten und halten sie für dich bereit. Sie wird bearbeitet, sobald deine Zahlung eingeht.</p>
            <p><strong>Bestellung:</strong> #{{ orderNumber }}<br/>
            <strong>Zu sendender Betrag:</strong> {{ orderTotal }}<br/>
            <strong>Empfänger:</strong> {{ zelleMerchantEmail }}<br/>
            <strong>Verwendungszweck / Referenz:</strong> #{{ orderNumber }}</p>
          `,
        },
      },
    },
  },
  variables: [
    { key: 'customerFirstName', label: 'Customer First Name', example: 'John' },
    { key: 'customerLastName', label: 'Customer Last Name', example: 'Doe' },
    { key: 'customerEmail', label: 'Customer Email', example: 'john@example.com' },
    { key: 'storeName', label: 'Store Name', example: 'My Store' },
    { key: 'orderNumber', label: 'Order Number', example: '1042' },
    { key: 'orderSubtotal', label: 'Order Subtotal', example: '$134.99' },
    { key: 'orderShipping', label: 'Order Shipping', example: '$14.99' },
    { key: 'orderTax', label: 'Order Tax', example: '$8.20' },
    { key: 'orderDiscount', label: 'Order Discount', example: '$5.00' },
    { key: 'orderTotal', label: 'Order Total', example: '$153.18' },
    { key: 'zelleMerchantEmail', label: 'Zelle Recipient Email', example: 'payments@mystore.com' },
    { key: 'orderReference', label: 'Payment Memo / Reference', example: '1042' },
    { key: 'paymentInstructions', label: 'Merchant Payment Instructions', example: 'Send within 24 hours.' },
    { key: 'orderItemsHtml', label: 'Order Items (HTML table)', example: '<table>...</table>' },
  ],
  utmParameters: {
    utmSource: 'order_pending',
    utmMedium: 'email',
  },
  specificParams: undefined,
  mockData: (storeName = 'Demo Store') => ({
    store: {
      id: 'store_preview',
      name: storeName,
      baseCurrency: 'USD',
      presentmentCurrencies: ['USD'],
      chargeCurrencies: ['USD'],
    },
    customer: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john@example.com',
      locale: 'en-US',
    },
    order: {
      id: 'ord_preview_1042',
      orderNumber: '1042',
      status: 'to_be_paid',
      paidAt: null,
      currency: 'USD',
      totalAmount: 14999,
      items: [
        { productName: 'Premium Widget', variantName: 'Large / Black', quantity: 2, unitAmount: 4999 },
        { productName: 'Starter Kit', variantName: '', quantity: 1, unitAmount: 5001 },
      ],
      customer: { firstName: 'John', lastName: 'Doe', email: 'john@example.com', locale: 'en-US' },
      store: { id: 'store_preview', name: storeName },
      paymentMethod: { name: 'zelle' },
      paymentInstructions: {
        merchantEmail: 'payments@demostore.com',
        reference: '1042',
        instructions: 'Please send your payment within 24 hours so we can ship your order.',
      },
    },
  }),
  defaultMessages: {
    order_title: 'Complete Your Payment',
    order_subtitle: 'Your order is reserved until payment arrives',
    cta_button: 'View Order',
    footer_text: '© 2025 All rights reserved',
  },
};
